<header
	class="fixed top-0 left-0 right-0 py-6 px-6 md:py-7 md:px-8 transition-all duration-300 ease-out z-50"
	[ngClass]="{
		'bg-mist shadow-lg border-b border-charcoal/5': isScrolled && !isMobileMenuOpen,
		'bg-transparent': !isScrolled || isMobileMenuOpen
	}">
	<div class="flex items-center justify-between max-w-[1400px] mx-auto">
		<!-- Mobile Logo with Premium Styling -->
		<div class="md:hidden relative z-[110]">
			<app-logo
				[variant]="isScrolled ? 'dark' : (lightMode ? 'light' : 'dark')"
				[size]="'sm'"
				[isLink]="true">
			</app-logo>
		</div>

		<!-- Desktop Navigation with Premium Styling -->
		<nav class="hidden md:flex items-center gap-8 lg:gap-10">
			<!-- Services with Mega Menu -->
			<div class="relative" (mouseleave)="closeMegaMenu()">
				<a routerLink="/services"
					(mouseenter)="openMegaMenu()"
					[ngClass]="isScrolled ? 'text-charcoal hover:text-primary' : (lightMode ? 'text-mist/90 hover:text-secondary' : 'text-charcoal hover:text-primary')"
					class="relative font-[900] lg:text-lg transition-colors duration-300 tracking-wider group flex items-center gap-1.5 py-2">
					SERVICES
					<svg class="w-4 h-4 transition-transform duration-300" [class.rotate-180]="isMegaMenuOpen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
					</svg>
					<span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary to-primary transition-all duration-300 group-hover:w-full"></span>
				</a>

				<!-- Mega Menu for Services - No gap between trigger and menu -->
				<div
					class="absolute top-full left-0 pt-4 z-50 w-[1100px]"
					[ngClass]="{
						'transition-all duration-300 ease-out': isMegaMenuReady,
						'opacity-100 visible translate-y-0 pointer-events-auto': isMegaMenuOpen && isMegaMenuReady,
						'opacity-0 invisible -translate-y-4 pointer-events-none': !isMegaMenuOpen || !isMegaMenuReady
					}">

					<div class="relative rounded-3xl overflow-hidden shadow-2xl">
						<!-- Primary Background -->
						<div class="absolute inset-0 bg-primary"></div>

						<!-- Decorative Elements -->
						<div class="absolute inset-0 overflow-hidden pointer-events-none">
							<div class="absolute -top-32 -right-32 w-96 h-96 bg-secondary/15 rounded-full blur-3xl"></div>
							<div class="absolute -bottom-32 -left-32 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
						</div>

						<!-- Subtle Pattern Overlay -->
						<div class="absolute inset-0 opacity-[0.03]" style="background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0); background-size: 20px 20px;"></div>

						<div class="relative p-8">
							<!-- Header -->
							<div class="flex items-center justify-between mb-8">
								<div class="flex items-center gap-4">
									<div class="w-12 h-px bg-gradient-to-r from-secondary to-transparent"></div>
									<h3 class="text-white font-cenzo text-2xl font-bold">Our Services</h3>
								</div>
								<a routerLink="/services" (click)="closeMegaMenu()"
									class="group flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 transition-all duration-300">
									<span class="text-white font-semibold text-sm">View All Services</span>
								</a>
							</div>

							<!-- Category Image Cards Grid -->
							<div class="grid grid-cols-3 gap-6 mb-8">
								@for (category of categories(); track category.id) {
									<a [routerLink]="['/services']" [queryParams]="{category: category.slug}"
										(click)="closeMegaMenu()"
										(mouseenter)="setActiveCategory(category)"
										class="group relative h-[280px] rounded-2xl overflow-hidden cursor-pointer border-2 transition-all duration-500"
										[ngClass]="activeCategory()?.id === category.id
											? 'border-secondary shadow-xl shadow-secondary/20 scale-[1.02]'
											: 'border-white/10 hover:border-secondary/50'">

										<!-- Category Image -->
										<img
											[src]="getCategoryImage(category)"
											[alt]="category.name"
											class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />

										<!-- Gradient Overlay -->
										<div class="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500"></div>

										<!-- Active Indicator -->
										@if (activeCategory()?.id === category.id) {
											<div class="absolute top-4 right-4 w-3 h-3 bg-secondary rounded-full animate-pulse"></div>
										}

										<!-- Content -->
										<div class="absolute bottom-0 left-0 right-0 p-6">
											<!-- Decorative Line -->
											<div class="flex items-center gap-2 mb-3 transition-all duration-500"
												[ngClass]="activeCategory()?.id === category.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'">
												<div class="w-8 h-0.5 bg-secondary"></div>
												<span class="text-secondary text-xs font-semibold tracking-widest uppercase">Explore</span>
											</div>

											<!-- Category Name -->
											<h4 class="font-cenzo text-2xl font-bold text-white mb-2 transition-transform duration-500 group-hover:-translate-y-1">
												{{ category.name }}
											</h4>

											<!-- Description -->
											@if (category.description) {
												<p class="text-white/70 text-sm leading-relaxed line-clamp-2 mb-4 transition-all duration-500"
													[ngClass]="activeCategory()?.id === category.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'">
													{{ category.description }}
												</p>
											}

											<!-- Services Count & Arrow -->
											<div class="flex items-center justify-between">
												@if (category.services_count) {
													<span class="text-secondary text-sm font-medium">{{ category.services_count }} services</span>
												}
												<div class="w-10 h-10 rounded-full bg-secondary/20 border border-secondary/40 flex items-center justify-center transition-all duration-300 group-hover:bg-secondary group-hover:scale-110">
													<svg class="w-5 h-5 text-secondary group-hover:text-primary transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
													</svg>
												</div>
											</div>
										</div>

										<!-- Corner Accent -->
										<div class="absolute top-4 left-4 w-12 h-12 border-t-2 border-l-2 transition-all duration-500"
											[ngClass]="activeCategory()?.id === category.id ? 'border-secondary' : 'border-white/20 group-hover:border-secondary/50'"></div>
									</a>
								} @empty {
									<!-- Loading Skeleton -->
									@for (i of [1,2,3]; track i) {
										<div class="h-[280px] rounded-2xl bg-white/5 animate-pulse"></div>
									}
								}
							</div>

							<!-- Services Preview for Active Category -->
							@if (activeCategory() && activeCategory()!.services && activeCategory()!.services!.length > 0) {
								<div class="bg-white/5 rounded-2xl p-6 border border-white/10">
									<div class="flex items-center justify-between mb-4">
										<div class="flex items-center gap-3">
											<div class="w-8 h-px bg-secondary"></div>
											<span class="text-secondary text-sm font-semibold tracking-wide">Popular in {{ activeCategory()!.name }}</span>
										</div>
										<a [routerLink]="['/services']" [queryParams]="{category: activeCategory()!.slug}" (click)="closeMegaMenu()"
											class="text-white/60 hover:text-secondary text-sm font-medium transition-colors duration-300">
											See all
										</a>
									</div>

									<div class="grid grid-cols-4 gap-4">
										@for (service of activeCategory()!.services!.slice(0, 4); track service.id) {
											<a [routerLink]="['/services', service.slug]" (click)="closeMegaMenu()"
												class="group p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-secondary/30 transition-all duration-300">
												<h5 class="text-white font-semibold text-sm group-hover:text-secondary transition-colors duration-300 truncate">
													{{ service.service_name }}
												</h5>
											</a>
										}
									</div>
								</div>
							}

							<!-- Bottom CTA Bar -->
							<div class="mt-6 pt-6 border-t border-white/10">
								<div class="flex items-center justify-between">
									<div class="flex items-center gap-4">
										<div class="w-12 h-12 rounded-full bg-gradient-to-br from-secondary/30 to-secondary/10 border border-secondary/30 flex items-center justify-center">
											<svg class="w-6 h-6 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
											</svg>
										</div>
										<div>
											<p class="text-white font-semibold">Ready to transform your look?</p>
											<p class="text-white/50 text-sm">Book a free consultation with our experts</p>
										</div>
									</div>
									<button (click)="openBookingModal()" class="px-8 py-4 rounded-full bg-secondary hover:bg-white text-primary font-bold tracking-wider shadow-xl hover:shadow-secondary/30 hover:scale-105 transition-all duration-300">
										BOOK CONSULTATION
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<a routerLink="/blog"
				[ngClass]="isScrolled ? 'text-charcoal hover:text-primary' : (lightMode ? 'text-mist/90 hover:text-secondary' : 'text-charcoal hover:text-primary')"
				class="relative font-[900] lg:text-lg transition-colors duration-300 tracking-wider group">
				BLOG
				<span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary to-primary transition-all duration-300 group-hover:w-full"></span>
			</a>
			<a routerLink="/about"
				[ngClass]="isScrolled ? 'text-charcoal hover:text-primary' : (lightMode ? 'text-mist/90 hover:text-secondary' : 'text-charcoal hover:text-primary')"
				class="relative font-[900] lg:text-lg transition-colors duration-300 tracking-wider group">
				ABOUT
				<span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary to-primary transition-all duration-300 group-hover:w-full"></span>
			</a>
		</nav>

		<!-- Desktop Center Logo with Premium Badge Design -->
		<div class="hidden md:block absolute left-1/2 transform -translate-x-1/2">
			<app-logo
				[variant]="isScrolled ? 'dark' : (lightMode ? 'light' : 'dark')"
				[size]="'md'"
				[isLink]="true">
			</app-logo>
		</div>

		<!-- Desktop Buttons - Login & Book Now with Premium Design -->
		<div class="hidden md:flex items-center gap-4">
			<!-- Login Button -->
			@if (!hideLoginButton()) {
			<a
				routerLink="/login"
				[ngClass]="{
					'text-primary hover:text-primary-75': isScrolled || !lightMode,
					'text-mist hover:text-secondary': lightMode && !isScrolled
				}"
				class="relative px-5 py-2.5 lg:px-7 lg:py-3 rounded-lg font-bold text-base lg:text-lg tracking-wider transition-all duration-300 flex items-center gap-2.5 group">
				<svg class="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
				</svg>
				<span>LOGIN</span>
			</a>
			}

			<!-- Book Now Button -->
			<button
				(click)="openBookingModal()"
				[ngClass]="{
					'border-charcoal/30 text-primary': isScrolled || !lightMode,
					'border-mist/40 text-mist': lightMode && !isScrolled
				}"
				class="relative px-7 py-3 lg:px-9 lg:py-3.5 rounded-lg font-bold text-base lg:text-lg tracking-wider overflow-hidden border-2 transition-all duration-300 btn-slide">
				<span class="relative z-10 flex items-center gap-2.5">
					BOOK NOW
				</span>
			</button>
		</div>

		<!-- Mobile Menu Button with Premium Animation -->
		<button
			(click)="toggleMobileMenu()"
			class="md:hidden relative z-10 w-10 h-10 flex flex-col justify-center items-center rounded-lg transition-all duration-300"
			[ngClass]="isScrolled ? 'text-charcoal hover:bg-charcoal/5' : (lightMode ? 'text-mist hover:bg-white/10' : 'text-charcoal hover:bg-charcoal/5')">
			<span class="w-5 h-0.5 bg-current transition-all duration-300 ease-in-out rounded-full"
				[ngClass]="{
					'rotate-45 translate-y-1.5': isMobileMenuOpen,
					'rotate-0 translate-y-0': !isMobileMenuOpen
				}"></span>
			<span class="w-5 h-0.5 bg-current transition-all duration-300 ease-in-out rounded-full my-1"
				[ngClass]="{
					'opacity-0 scale-0': isMobileMenuOpen,
					'opacity-100 scale-100': !isMobileMenuOpen
				}"></span>
			<span class="w-5 h-0.5 bg-current transition-all duration-300 ease-in-out rounded-full"
				[ngClass]="{
					'-rotate-45 -translate-y-1.5': isMobileMenuOpen,
					'rotate-0 translate-y-0': !isMobileMenuOpen
				}"></span>
		</button>
	</div>

</header>

<!-- Mobile Menu Backdrop with Glass Effect -->
<div
	class="md:hidden fixed inset-0 bg-black/90 backdrop-blur-md z-40 pt-20 transition-all duration-500 ease-in-out"
	[ngClass]="{
		'opacity-100 pointer-events-auto': isMobileMenuOpen,
		'opacity-0 pointer-events-none': !isMobileMenuOpen
	}"
	(click)="toggleMobileMenu()">
</div>

<!-- Mobile Menu with Premium Design -->
<nav
	class="md:hidden fixed top-20 left-0 right-0 bottom-0 z-50 flex flex-col transition-all duration-500 ease-in-out overflow-y-auto"
	[ngClass]="{
		'translate-y-0 opacity-100 pointer-events-auto': isMobileMenuOpen,
		'translate-y-8 opacity-0 pointer-events-none': !isMobileMenuOpen
	}">

	<div class="flex flex-col px-5 py-6">
		<!-- Services Section with Large Image Cards -->
		<div class="mb-6">
			<!-- Section Header -->
			<div class="flex items-center justify-between mb-4">
				<div class="flex items-center gap-3">
					<div class="w-8 h-px bg-secondary"></div>
					<span class="text-secondary text-sm font-semibold tracking-widest uppercase">Our Services</span>
				</div>
				<a routerLink="/services" (click)="toggleMobileMenu()"
					class="text-white/60 text-sm font-medium">View All</a>
			</div>

			<!-- Category Image Cards - Vertical Stack for Better Touch -->
			@if (categories().length > 0) {
				<div class="space-y-4">
					@for (category of categories(); track category.id) {
						<a [routerLink]="['/services']" [queryParams]="{category: category.slug}"
							(click)="toggleMobileMenu()"
							class="group relative block h-[160px] rounded-2xl overflow-hidden border border-white/10 active:scale-[0.98] transition-all duration-300 cursor-pointer">

							<!-- Category Image -->
							<img [src]="getCategoryImage(category)" [alt]="category.name"
								class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-active:scale-105 pointer-events-none" />

							<!-- Gradient Overlay -->
							<div class="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent pointer-events-none"></div>

							<!-- Content - Left Aligned -->
							<div class="absolute inset-0 flex items-center p-5 pointer-events-none">
								<div class="flex-1">
									<!-- Decorative Line -->
									<div class="flex items-center gap-2 mb-2">
										<div class="w-6 h-0.5 bg-secondary"></div>
										<span class="text-secondary text-xs font-semibold tracking-wider uppercase">Explore</span>
									</div>

									<!-- Category Name -->
									<h4 class="font-cenzo text-2xl font-bold text-white mb-1">
										{{ category.name }}
									</h4>

									<!-- Services Count -->
									@if (category.services_count) {
										<span class="text-white/60 text-sm">{{ category.services_count }} services available</span>
									}
								</div>

								<!-- Arrow Button -->
								<div class="w-12 h-12 rounded-full bg-secondary/20 border border-secondary/40 flex items-center justify-center flex-shrink-0">
									<svg class="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
									</svg>
								</div>
							</div>

							<!-- Corner Accent -->
							<div class="absolute top-3 left-3 w-8 h-8 border-t-2 border-l-2 border-secondary/40 pointer-events-none"></div>
						</a>
					}
				</div>
			} @else {
				<!-- Loading State -->
				<div class="space-y-4">
					@for (i of [1,2]; track i) {
						<div class="h-[160px] rounded-2xl bg-white/5 animate-pulse"></div>
					}
				</div>
			}
		</div>

		<!-- Other Navigation Links -->
		<div class="space-y-3 mb-6">
			<a routerLink="/blog" (click)="toggleMobileMenu()"
				class="group flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 active:bg-white/10 transition-all duration-300">
				<div class="flex items-center gap-3">
					<div class="w-10 h-10 rounded-lg bg-primary/30 flex items-center justify-center">
						<svg class="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
						</svg>
					</div>
					<span class="text-white text-lg font-semibold">Blog</span>
				</div>
				<svg class="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
				</svg>
			</a>

			<a routerLink="/about" (click)="toggleMobileMenu()"
				class="group flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 active:bg-white/10 transition-all duration-300">
				<div class="flex items-center gap-3">
					<div class="w-10 h-10 rounded-lg bg-primary/30 flex items-center justify-center">
						<svg class="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
						</svg>
					</div>
					<span class="text-white text-lg font-semibold">About Us</span>
				</div>
				<svg class="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
				</svg>
			</a>

			@if (!hideLoginButton()) {
			<a routerLink="/login" (click)="toggleMobileMenu()"
				class="group flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 active:bg-white/10 transition-all duration-300">
				<div class="flex items-center gap-3">
					<div class="w-10 h-10 rounded-lg bg-primary/30 flex items-center justify-center">
						<svg class="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
						</svg>
					</div>
					<span class="text-white text-lg font-semibold">Login</span>
				</div>
				<svg class="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
				</svg>
			</a>
			}
		</div>
	</div>

	<!-- Fixed Bottom CTA -->
	<div class="mt-auto p-5 bg-gradient-to-t from-black via-black/95 to-transparent">
		<button
			(click)="openBookingModal()"
			class="w-full relative bg-gradient-to-r from-secondary to-primary text-primary px-8 py-5 rounded-2xl font-bold text-lg tracking-wider shadow-2xl active:scale-[0.98] transition-all duration-300">
			<span class="flex items-center justify-center gap-3">
				<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
				</svg>
				BOOK CONSULTATION
			</span>
		</button>

		<!-- Contact Info -->
		<div class="flex items-center justify-center gap-6 mt-4">
			<a href="tel:+919876543210" class="flex items-center gap-2 text-white/60 text-sm">
				<svg class="w-4 h-4 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
				</svg>
				Call Us
			</a>
			<div class="w-px h-4 bg-white/20"></div>
			<a href="#location" class="flex items-center gap-2 text-white/60 text-sm">
				<svg class="w-4 h-4 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
				</svg>
				Visit Studio
			</a>
		</div>
	</div>
</nav>
